import { useState, useEffect, useMemo, useCallback } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { usePedidoMovement } from "@/hooks/usePedidoMovement";
import { useAuth } from "@/hooks/useAuth";
import { useStatusCatalogo } from "@/hooks/useStatusCatalogo";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AppIcons, IconSizes, getQuadroIcon } from "@/lib/icons";
import { QUADRO_LABELS, isStatusBloqueadoTriagem, getTriagemDaColuna } from "@/lib/quadro-utils";
import { cn } from "@/lib/utils";

type Props = {
  pedidoId: string;
  itemId?: string;
  quadro: string;
  coluna: string;
  statusNome?: string;
  statusCor?: string;
  onStatusChange?: () => void;
  className?: string;
};

type TransicaoValida = {
  para_coluna: string;
  para_quadro: string;
  status_destino_id: string | null;
  status_destino_nome: string;
  status_destino_cor?: string;
};

export function StatusBadgeInteractive({ 
  pedidoId,
  itemId,
  quadro, 
  coluna, 
  statusNome,
  statusCor,
  onStatusChange,
  className 
}: Props) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);
  const { moveCard, isMoving } = usePedidoMovement();
  const { profile } = useAuth();
  const { status: todosStatus, isLoading: isLoadingStatus } = useStatusCatalogo();
  const { toast } = useToast();
  const userRole = profile?.role;

  // v3 (README-V3 §3.1): chip NEUTRO — a cor vive no dot, nunca no chip.
  // ⛔ o TEXTO do status nunca muda (chave literal no banco).
  const chipNeutroV3 = "bg-muted/70 dark:bg-muted text-muted-foreground border-transparent font-bold tracking-[0.4px]";
  const dotQuadroV3 =
    /REPROV|DIVERG|PENDEN/i.test(coluna || '') ? 'bg-danger'
    : quadro === 'QUALIDADE' ? 'bg-success'
    : quadro === 'PEDIDO_CANCELADO' ? 'bg-muted-foreground/40'
    : 'bg-muted-foreground/60';

  // Coluna com triagem: o status anda pela triagem. Somente master conserva a
  // correção excepcional e auditada; os demais nunca atravessam pelo selo.
  const triagemDaColuna = getTriagemDaColuna(quadro, coluna);
  const isMaster = userRole === 'master';
  const travadoPelaTriagem = !!triagemDaColuna && !isMaster;
  const correcaoMaster = !!triagemDaColuna && isMaster;

  // Filtrar transicoes validas de forma sincrona a partir do cache React Query
  const transicoesValidas = useMemo(() => {
    if (travadoPelaTriagem) return [];
    if (!todosStatus || !userRole) return [];

    // Filtrar apenas ativos
    const ativos = todosStatus.filter(s => s.ativo && !(s.quadro === 'QUALIDADE' && ['ATIVAÇÃO DE CHIP', 'BOAS-VINDAS', 'ACESSO AO PORTAL', 'PRIMEIRA FATURA'].includes(s.coluna)));

    // Aplicar filtros baseados no quadro atual
    let statusFiltrados = [];
    
    if (quadro === 'COMERCIAL') {
      statusFiltrados = ativos.filter(s => 
        (s.quadro === 'COMERCIAL' && s.coluna !== coluna) ||
        (isMaster && s.coluna === 'PEDIDO FECHADO')
      );
    } else {
      statusFiltrados = ativos.filter(s => 
        s.quadro !== 'COMERCIAL' && 
        s.quadro !== 'PEDIDO_CANCELADO' &&
        s.coluna !== coluna
      );
    }
    
    if (!isMaster) {
      statusFiltrados = statusFiltrados.filter(s => !isStatusBloqueadoTriagem(s.nome, s.quadro));
    }

    // Filtro especial: ERRO SOLAR INPUT MANUAL -> apenas VALIDACAO PENDENTE
    const colunaNormalizada = coluna.replace(/\s+/g, ' ').trim();
    if (colunaNormalizada === 'ERRO SOLAR INPUT MANUAL') {
      statusFiltrados = statusFiltrados.filter(s => s.coluna === 'VALIDAÇÃO PENDENTE');
    }

    // Filtro especial: NEGOCIACAO (ERRO DE INPUT) -> apenas VALIDACAO PENDENTE
    if (colunaNormalizada === 'NEGOCIAÇÃO (ERRO DE INPUT)') {
      statusFiltrados = statusFiltrados.filter(s => s.coluna === 'VALIDAÇÃO PENDENTE');
    }

    // Filtro especial: CONTRATO_PRIME -> livre no quadro + 1a saida obrigatoria
    if (quadro === 'CONTRATO_PRIME') {
      statusFiltrados = statusFiltrados.filter(s => 
        s.quadro === 'CONTRATO_PRIME' ||
        s.coluna === 'INPUT REALIZADO MOVEL' ||
        s.coluna === 'INPUT REALIZADO FIXA'
      );
    }
    
    // Suporte Comercial anda somente pelas ações/triagens oficiais.
    if (!isMaster && quadro === 'CONTRATO_PRIME' && ['PEDIDO FECHADO', 'PEDIDO RECUPERADO', 'CRIAÇÃO PEDIDO', 'BIOMETRIA', 'ACEITE', 'INPUT', 'PENDENCIA INPUT', 'AGUARDANDO HP'].includes(colunaNormalizada)) {
      statusFiltrados = [];
    }

    // Filtro de role (se nao for MASTER)
    if (userRole !== 'master') {
      statusFiltrados = statusFiltrados.filter(s => {
        const rolesBloqueadas = (s as any).roles_bloqueadas || [];
        return !rolesBloqueadas.includes(userRole);
      });
    }
    
    // Converter para formato TransicaoValida
    return statusFiltrados.map(s => ({
      para_coluna: s.coluna,
      para_quadro: s.quadro,
      status_destino_id: s.id,
      status_destino_nome: s.nome,
      status_destino_cor: s.cor,
    }));
  }, [todosStatus, quadro, coluna, userRole, travadoPelaTriagem, isMaster]);

  // Cancelados nao podem ter status alterado
  const isCancelado = quadro === 'PEDIDO_CANCELADO';

  const handleStatusChange = useCallback(async (transicao: TransicaoValida) => {
    if (isUpdating || isMoving) return;
    
    // Validacao: NEGOCIACAO (ERRO DE INPUT) -> cotacao obrigatoria
    const colunaNorm = coluna.replace(/\s+/g, ' ').trim();
    if (colunaNorm === 'NEGOCIAÇÃO (ERRO DE INPUT)') {
      try {
        const targetId = itemId || pedidoId;
        const { data: registro } = await supabase
          .from('v_planilha_mae')
          .select('numero_cotacao_operadora')
          .eq(itemId ? 'item_id' : 'pedido_id', targetId)
          .limit(1)
          .maybeSingle();
        
        if (!registro?.numero_cotacao_operadora) {
          toast({
            variant: "destructive",
            title: "Cotacao Obrigatoria",
            description: "Preencha o campo Cotacao (Dados Operadora) antes de mover o pedido de Erro de Input.",
            duration: 8000,
          });
          return;
        }
      } catch (err) {
        console.error('Erro ao validar cotacao:', err);
      }
    }
    
    setIsUpdating(true);
    setIsDropdownOpen(false);
    setDropdownKey(prev => prev + 1);
    
    moveCard({
      pedidoId,
      itemId,
      toQuadro: transicao.para_quadro as any,
      toColuna: transicao.para_coluna,
      toStatusId: transicao.status_destino_id,
      motivo: correcaoMaster
        ? `Correção master (fora da triagem ${triagemDaColuna}): ${quadro}:${coluna} → ${transicao.para_quadro}:${transicao.para_coluna}`
        : `Movido de ${quadro}:${coluna} para ${transicao.para_quadro}:${transicao.para_coluna}`,
    })
      .then(() => {
        onStatusChange?.();
      })
      .catch((error) => {
        console.error('[StatusBadgeInteractive] Erro ao trocar status:', error.message);
        
        toast({
          variant: "destructive",
          title: "Erro ao Mudar Status",
          description: error.message,
          duration: 10000,
        });
        
        setTimeout(() => {
          setIsDropdownOpen(false);
          setIsUpdating(false);
          setDropdownKey(prev => prev + 1);
        }, 100);
      })
      .finally(() => {
        setIsUpdating(false);
        setIsDropdownOpen(false);
      });
  }, [isUpdating, isMoving, coluna, itemId, pedidoId, quadro, moveCard, onStatusChange, toast]);

  if (isCancelado) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="outline" 
              className={cn(
                "text-[10px] flex items-center gap-1 px-2 py-0.5 cursor-not-allowed opacity-80 max-w-full overflow-hidden",
                chipNeutroV3,
                className
              )}
            >
              <span className={cn("h-[5px] w-[5px] rounded-full flex-shrink-0", dotQuadroV3)} />
              <span className="truncate">PEDIDO CANCELADO</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium">Item Cancelado</p>
            <p className="text-xs text-muted-foreground">
              Para recuperar este item, use o botao "Recuperar" no painel do pedido ou na pagina de Cancelamentos.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (travadoPelaTriagem) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] flex items-center gap-1 px-2 py-0.5 cursor-default max-w-full overflow-hidden",
                chipNeutroV3,
                className
              )}
            >
              <span className={cn("h-[5px] w-[5px] rounded-full flex-shrink-0", dotQuadroV3)} />
              <span className="truncate">{statusNome || coluna}</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium">Trabalhe pela {triagemDaColuna}</p>
            <p className="text-xs text-muted-foreground">
              {coluna} ({QUADRO_LABELS[quadro as keyof typeof QUADRO_LABELS] || quadro}) se move só pela triagem — clique no
              cabeçalho da coluna no Kanban. Movimento fora da triagem não entra no relatório do BKO.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Se ainda esta carregando status ou role, mostrar loading
  if (isLoadingStatus || !userRole) {
    return (
      <Badge 
        variant="outline" 
        className={cn("text-[10px] flex items-center gap-1 px-2 py-0.5 whitespace-nowrap", chipNeutroV3, className)}
      >
        <span className={cn("h-[5px] w-[5px] rounded-full flex-shrink-0", dotQuadroV3)} />
        <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
        <span>{statusNome || coluna}</span>
      </Badge>
    );
  }

  // Se e MASTER ou tem transicoes validas -> Badge CLICAVEL
  if (transicoesValidas.length > 0) {
    return (
    <DropdownMenu 
      key={dropdownKey}
      open={isDropdownOpen} 
      onOpenChange={setIsDropdownOpen}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={isMoving || isUpdating}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition-all hover:shadow-sm cursor-pointer whitespace-nowrap",
            chipNeutroV3,
            className
          )}
          title="Clique para mudar status"
          style={{
            pointerEvents: 'auto',
            position: 'relative',
            // z-index 50 fazia o selo pintar POR CIMA do cabecalho sticky (z-15) da
            // Planilha ao rolar. 1 basta pra ganhar o clique dentro da linha.
            zIndex: 1,
            cursor: 'pointer'
          }}
        >
          <span className={cn("h-[5px] w-[5px] rounded-full flex-shrink-0", dotQuadroV3)} />
          {isMoving || isUpdating ? (
            <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
          ) : (
            <ChevronDown className="h-3 w-3 flex-shrink-0" />
          )}
          <span>{statusNome || coluna}</span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuPortal>
        <DropdownMenuContent 
          align="start" 
          side="bottom"
          sideOffset={8}
          alignOffset={0}
          collisionPadding={20}
          className="w-[280px] max-h-[400px] overflow-y-auto border bg-popover shadow-lg"
          style={{ zIndex: 10800 }}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {/* Correção do master fora da triagem: avisa ANTES, não depois. */}
          {correcaoMaster && (
            <div className="mx-1 mt-1 mb-1.5 rounded-md border border-primary/40 bg-primary/10 px-2 py-1.5">
              <p className="text-[11px] font-semibold text-primary">Correção de master — fora da {triagemDaColuna}</p>
              <p className="text-[10px] leading-snug text-muted-foreground">
                Use para acertar item que conta errado (ex.: consta ativo sem ter ativado). Fica registrado no
                seu nome na timeline e não entra no relatório do BKO.
              </p>
            </div>
          )}
          <div className="px-2 py-1.5 text-xs text-muted-foreground font-medium">
            {quadro === 'COMERCIAL'
              ? 'Status disponiveis no COMERCIAL:'
              : userRole === 'master' ? 'Mover para (MASTER - Todos os status):' : 'Mover para:'}
          </div>
          
          {Object.entries(
            transicoesValidas.reduce((acc, t) => {
              if (!acc[t.para_quadro]) acc[t.para_quadro] = [];
              acc[t.para_quadro].push(t);
              return acc;
            }, {} as Record<string, TransicaoValida[]>)
          ).sort(([quadroA], [quadroB]) => {
            const ordemQuadros = [
              'COMERCIAL',
              'CONTRATO_PRIME',
              'TRAMITACAO_VIVO',
              'TRAMITACAO_PRIME',
              'PRE_ATIVO',
              'QUALIDADE',
              'PEDIDO_CANCELADO'
            ];
            const indexA = ordemQuadros.indexOf(quadroA);
            const indexB = ordemQuadros.indexOf(quadroB);
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return quadroA.localeCompare(quadroB);
          }).map(([quadroDestino, transicoes]) => (
            <div key={quadroDestino}>
              {quadro !== 'COMERCIAL' && (
                <div className="px-2 py-1 text-xs font-semibold text-muted-foreground border-t mt-1 first:border-t-0 first:mt-0">
                  {QUADRO_LABELS[quadroDestino as keyof typeof QUADRO_LABELS] || quadroDestino}
                </div>
              )}
              {transicoes.map((transicao) => {
                const QuadroIcon = getQuadroIcon(transicao.para_quadro).icon;
                return (
                  <DropdownMenuItem
                    key={transicao.status_destino_id}
                    onClick={() => handleStatusChange(transicao)}
                    className="cursor-pointer"
                  >
                    <Badge 
                      variant="outline" 
                      className={`text-xs flex items-center gap-1.5 w-full justify-start ${getStatusColor(transicao.para_quadro)}`}
                    >
                      <QuadroIcon className={IconSizes.xs} />
                      {transicao.status_destino_nome}
                    </Badge>
                  </DropdownMenuItem>
                );
              })}
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
    );
  }

  // Badge NAO CLICAVEL (usuarios sem permissao)
  return (
    <Badge 
      variant="outline" 
      className={cn("text-[10px] flex items-center gap-1 px-2 py-0.5 max-w-full overflow-hidden", chipNeutroV3, className)}
    >
      <span className={cn("h-[5px] w-[5px] rounded-full flex-shrink-0", dotQuadroV3)} />
      <span className="truncate">{statusNome || coluna}</span>
    </Badge>
  );
}

function getStatusColor(quadro: string) {
  const colors: Record<string, string> = {
    'COMERCIAL': 'bg-primary/10 text-primary border-primary dark:bg-primary/20 dark:text-primary',
    'CONTRATO_PRIME': 'bg-primary/10 text-primary border-primary dark:bg-primary/20 dark:text-primary',
    'TRAMITACAO_VIVO': 'bg-primary/10 text-primary border-primary dark:bg-primary/20 dark:text-primary',
    'TRAMITACAO_PRIME': 'bg-muted text-primary border-border dark:bg-muted/20 dark:text-primary',
    'QUALIDADE': 'bg-success/10 text-success border-success dark:bg-success/20 dark:text-success',
    'PEDIDO_CANCELADO': 'bg-danger/10 text-danger border-danger dark:bg-danger/20 dark:text-danger',
  };
  return colors[quadro] || 'bg-muted text-foreground border-border dark:bg-muted/20 dark:text-muted-foreground';
}
